import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createCamera } from "../api/create-camera";
import { deleteCamera } from "../api/delete-camera";
import { updateCameraAdjustment } from "../api/update-camera-adjustment";
import { updateCameraAnalysisNumber } from "../api/update-camera-analysis-number";
import { updateCameraImage } from "../api/update-camera-image";
import { updateCameraMapping } from "../api/update-camera-mapping";
import { CAMERAS_QUERY_KEY } from "./use-cameras";

export function useCreateCamera(branchUid: string, versionId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { previewImage?: File; extraId?: string }) =>
      createCamera({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useUpdateCameraImage(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { cameraOrientationId: number; previewImage: File }) =>
      updateCameraImage({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useUpdateCameraMapping(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      cameraOrientationId: number;
      floorPlanId: number | null;
      position: { x: number; y: number; rotation: number } | null;
      mappingType: "default" | "add" | "edit";
      additionId?: string | null;
      adjustmentId?: string | null;
    }) => updateCameraMapping({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useUpdateCameraAnalysisNumber(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { cameraOrientationId: number; analysisNumber: string | null }) =>
      updateCameraAnalysisNumber({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useUpdateCameraAdjustment(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      cameraOrientationId: number;
      adjustment: {
        adjustmentId: string;
        position: { x: number; y: number; rotation: number };
        frame: { dx: number; dy: number };
      } | null;
    }) => updateCameraAdjustment({ branchUid, versionId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    },
  });
}

export function useDeleteCamera(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cameraOrientationId: number) =>
      deleteCamera({ branchUid, versionId, cameraOrientationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
    },
  });
}
